/**
 * NEN-EN-ISO 14688 grondclassificatie, zoals TerraIndex hem aanbiedt ('NEN-ISO-14688').
 *
 * BASIS = NEN 6693. Die norm bouwt zelf voort op ISO 14688, en de keuzelijst van TerraIndex voor
 * ISO 14688 is vrijwel die van NEN 6693: dezelfde zandfracties (fijn 63-105 … grof), silt in
 * plaats van leem, dezelfde organische grondsoorten. Deze module ERFT daarom alles van `nen6693`
 * (`export *`) en overschrijft alleen wat ISO anders doet. Een ISO-laag bewaart zo dezelfde
 * interne codes als een NEN 6693-laag (Z, S, K, Gr, s, z, mediaantermen, kleuren, BZB), en het
 * laagformulier, de AI-verwerking en de TerraIndex-vertaling werken voor beide normen.
 *
 * Wat ISO anders doet (NEN-EN-ISO 14688-1/-2+A1+C11:2016 en de TerraIndex-lijst):
 *  - hoofdbestanddeel: de TerraIndex-lijst. Keitjes erbij (zeer grove grond); gyttja, slib, rots
 *    en bodemvreemde laag eruit. Water blijft, onder 'overige'.
 *  - bijmengingen: grindig, zandig, siltig, kleiig, keiig, altijd zwak/matig/sterk (NA.3: klasse
 *    1/2/3). Humeus is geen bijmenging maar het aparte kenmerk 'organisch' (zwak/sterk).
 *  - geen mineraalarm; consistentie alleen bij klei en silt, met de ISO-termen (deel 1, 5.14).
 *  - kleur: de NEN 6693-kleuren plus roze (NA.4).
 *  - eigen kenmerken: organisch, korrelhoekigheid en vorm (deel 1, tabel 4).
 *  - notatie: de bijmenging VÓÓR de hoofdfractie, met ISO-codes: `si1Sa`, `gr2Cl` (deel 1, 4.3.3).
 *
 * Bewust NIET overgenomen uit de nationale aanvulling: 'leem' voor Si (TerraIndex en NEN 6693
 * zeggen silt) en de NA-namen voor de zandfracties ('uiterst fijn' …). De korrelgrenzen zijn
 * dezelfde; alleen de woorden van NEN 6693 en TerraIndex winnen.
 */
import * as N from './nen6693.js';

export * from './nen6693.js';

// ─── 1. Hoofdbestanddeel ────────────────────────────────────────────────────

/** Keitjes: zeer grove grond. Eigen interne code, want NEN 6693 kent hem niet als grondsoort. */
export const KEITJES = 'KEI';

/** Grondsoorten (term → interne code). Dezelfde codes als NEN 6693, plus keitjes. */
export const GRONDSOORT = new Map<string, string>([
  ['grind', 'Gr'],
  ['zand', 'Z'],
  ['silt', 'S'],
  ['klei', 'K'],
  ['keitjes', KEITJES],
  ['veen', 'V'],
  ['teelaarde of humus', 'H'],
  ['detritus', 'DET'],
  ['bruinkool', 'BK'],
]);

/** Grondsoort → TI `LaSoilTypeCode`. Keitjes = TI 'P', teelaarde of humus = 'H' (beide nagelopen in een TerraIndex-export, 15 okt 2026). */
export const GRONDSOORT_TI: Record<string, string> = { ...N.GRONDSOORT_TI, [KEITJES]: 'P' };

/** Alleen water blijft over als laagtype zonder grond. */
export const LAAGTYPE_SPECIAAL = new Map<string, string>([['water', 'W']]);

export const LAAGTYPE_ZONDER_TEXTUUR = new Set(['W']);

export function acceptsAdmixtures(soilCode?: string | null): boolean {
  return !soilCode || !LAAGTYPE_ZONDER_TEXTUUR.has(soilCode);
}

/** De groepen in de keuzelijst, in de indeling van TerraIndex. */
export const HOOFDBESTANDDEEL_GROEPEN: ReadonlyArray<{
  label: string;
  items: ReadonlyArray<{ term: string; label: string }>;
}> = [
  {
    label: 'Organisch',
    items: [
      { term: 'veen', label: 'veen' },
      { term: 'teelaarde of humus', label: 'teelaarde of humus' },
      { term: 'detritus', label: 'detritus' },
      { term: 'bruinkool', label: 'bruinkool (organisch)' },
    ],
  },
  { label: 'Zeer grove grond', items: [{ term: 'keitjes', label: 'keitjes' }] },
  {
    label: 'Grove grond',
    items: [
      { term: 'grind', label: 'grind' },
      { term: 'zand', label: 'zand' },
    ],
  },
  {
    label: 'Fijne grond',
    items: [
      { term: 'silt', label: 'silt' },
      { term: 'klei', label: 'klei' },
    ],
  },
  { label: 'Overige', items: [{ term: 'water', label: 'water' }] },
];

/** Grondsoort-/laagtypecode → TI `LaSoilTypeCode`. */
export function soilTypeToTi(code: string | null | undefined): string | null {
  if (!code) return null;
  return GRONDSOORT_TI[code] ?? N.LAAGTYPE_SPECIAAL_TI[code] ?? null;
}

// ─── 2. Bijmengingen ────────────────────────────────────────────────────────

/** Bijmengingen (term → interne code). Codes van NEN 6693, plus keiig. Humeus: zie ORGANISCH. */
export const BIJMENGING = new Map<string, string>([
  ['grindig', 'gr'],
  ['kleiig', 'k'],
  ['keiig', 'ke'],
  ['siltig', 's'],
  ['zandig', 'z'],
]);

// ⚠ keiig → 'E' is nog niet nagelopen in een TerraIndex-export.
export const BIJMENGING_TI: Record<string, string> = { ...N.BIJMENGING_TI, ke: 'E' };

/** NA.3: elke bijmenging kent klasse 1/2/3. Kleiig dus ook, anders dan bij NEN 6693. */
export const INTENSITEIT = new Map<string, string>([['zwak', '1'], ['matig', '2'], ['sterk', '3']]);

const KLASSEN: readonly string[] = [...INTENSITEIT.keys()];

export const BIJMENGING_INTENSITEIT: Record<string, readonly string[]> = Object.fromEntries(
  [...BIJMENGING.values()].map((c) => [c, KLASSEN]),
);

/** De eigen bijmenging van een grondsoort — 'zandig zand' bestaat niet. */
export const GRONDSOORT_EIGEN_BIJMENGING: Record<string, string> = {
  Z: 'z', K: 'k', S: 's', Gr: 'gr', [KEITJES]: 'ke',
};

/** ISO kent de organische beperkingen van NEN 6693 (tabel 10) niet: drie bijmengingen overal. */
export function maxAdmixtures(_soilCode?: string | null): number {
  return 3;
}

export function blocksSecondTexture(): boolean {
  return false;
}

export function admixtureRule(_soilCode: string | null | undefined, admixture: string): N.AdmixtureRule {
  return BIJMENGING_INTENSITEIT[admixture]
    ? { allowed: true, intensities: KLASSEN, required: false }
    : { allowed: false, intensities: [], required: false };
}

export function admixtureToTi(code: string | null | undefined): string | null {
  return code ? BIJMENGING_TI[code] ?? null : null;
}

/** Klasse → TI-gradatie. Ook bij kleiig: ISO kent daar wel een klasse. */
export function intensityToTi(_admixtureCode: string | null | undefined, intensityLabel: string | null | undefined): string | null {
  return intensityLabel ? INTENSITEIT.get(intensityLabel) ?? null : null;
}

// ─── 3. Consistentie en mineraalarm ─────────────────────────────────────────

export const MINERAALARM_GRONDSOORTEN: ReadonlySet<string> = new Set();

/**
 * Consistentie bij klei en silt (de ISO-termen hieronder) en bij de organische grondsoorten.
 * Voor organisch biedt TerraIndex onder ISO 14688 precies de NEN 6693-lijst van tabel 23 aan
 * (zeer slap … vast; nagelopen in TerraIndex, 'slap' = code 1), dus die erven we van nen6693.
 */
export const ORGANISCHE_GRONDSOORTEN: ReadonlySet<string> = new Set(['V', 'H', 'DET', 'BK']);
export const ORGANISCHE_CONSISTENTIE_GRONDSOORTEN: ReadonlySet<string> = ORGANISCHE_GRONDSOORTEN;
export const MINERALE_CONSISTENTIE_GRONDSOORTEN: ReadonlySet<string> = new Set(['K', 'S']);
export const CONSISTENTIE_GRONDSOORTEN: ReadonlySet<string> = new Set([
  ...MINERALE_CONSISTENTIE_GRONDSOORTEN, ...ORGANISCHE_CONSISTENTIE_GRONDSOORTEN,
]);

/** Consistentie van cohesieve grond — deel 1, 5.14 (en deel 2, tabel 6). */
export const MINERALE_CONSISTENTIE: ReadonlyArray<{ term: string; description: string }> = [
  { term: 'zeer slap', description: 'Loopt tussen de vingers door als een monster in de hand wordt samengeknepen.' },
  { term: 'slap', description: 'Is met lichte druk van de vingers te kneden.' },
  { term: 'stevig', description: 'Niet met de vingers te kneden, wel met de hand tot strengen van 3 mm te rollen zonder te breken of te verkruimelen.' },
  { term: 'hard', description: 'Verkruimelt en breekt bij rollen tot strengen van 3 mm, maar is nog nat genoeg om weer tot een bol te kneden.' },
  { term: 'zeer hard', description: 'Uitgedroogd, meestal licht van kleur. Niet meer te vervormen, verkruimelt onder druk; met de duimnagel in te kerven.' },
];

/**
 * ISO-consistentie → de term in de TerraIndex-lijst (codelijst 48). Die kent 'hard' en 'zeer
 * hard' niet; dezelfde klassen heten daar, net als in NEN 6693, 'stijf' en 'zeer stijf'.
 */
export const CONSISTENTIE_NAAR_TI_TERM: Record<string, string> = { hard: 'stijf', 'zeer hard': 'zeer stijf' };

export function consistentieTerms(soilCode: string | null | undefined): string[] {
  if (soilCode && MINERALE_CONSISTENTIE_GRONDSOORTEN.has(soilCode)) return MINERALE_CONSISTENTIE.map((i) => i.term);
  if (soilCode && ORGANISCHE_CONSISTENTIE_GRONDSOORTEN.has(soilCode)) return N.ORGANISCHE_CONSISTENTIE.map((i) => i.term);
  return [];
}

// ─── 3b. Veentextuur ────────────────────────────────────────────────────────

/**
 * Veentextuur bij een organische grondsoort, zoals TerraIndex hem onder ISO 14688 aanbiedt. Een
 * verfijning van de mate van afbraak uit de norm (deel 1, tabel 2 en 5: vezelig, pseudovezelig,
 * amorf).
 */
export const VEENTEXTUUR: readonly string[] = [
  'fijn pseudo-vezelig', 'grof pseudo-vezelig', 'fijnvezelig', 'grofvezelig', 'amorf',
];

/**
 * Veentextuur → TI `LaPeatAmorphousCode`. Alleen fijnvezelig is nagelopen in een TerraIndex-export
 * (15 okt 2026); de rest volgt. Zonder code gaat het veld leeg mee in plaats van verkeerd.
 */
export const VEENTEXTUUR_TI: Record<string, string> = { fijnvezelig: '8' };

// ─── 4. Kleur ───────────────────────────────────────────────────────────────

/** De NEN 6693-kleuren plus roze (NA.4). */
export const KLEUR = new Map<string, string>([...N.KLEUR, ['roze', 'rz']]);

export const KLEUR_TI: Record<string, string> = { ...N.KLEUR_TI, rz: '17' };

export function colourToTi(code: string | null | undefined): string | null {
  return code ? KLEUR_TI[code] ?? null : null;
}

// ─── 5. Eigen kenmerken ─────────────────────────────────────────────────────

/** Organisch stofgehalte — deel 2, tabel 3; TerraIndex kent zwak en sterk. */
export const ORGANISCH: readonly string[] = ['zwak', 'sterk'];

/**
 * Waar 'organisch' bij hoort: minerale grond. Bij veen, humus, detritus of bruinkool is de laag zelf
 * de organische stof ('humeus veen' is een dubbeling), en TerraIndex biedt het veld daar ook niet aan.
 */
export function acceptsOrganic(soilCode: string | null | undefined): boolean {
  return !!soilCode && soilCode !== 'W' && !ORGANISCHE_GRONDSOORTEN.has(soilCode);
}

/** Korrelhoekigheid — deel 1, tabel 4 (afronding), in de volgorde van TerraIndex. */
export const KORRELHOEKIGHEID: readonly string[] = [
  'zeer hoekig', 'hoekig', 'subhoekig', 'subrond', 'afgerond', 'zeer afgerond',
];

/** Korrelvorm — deel 1, tabel 4, in de woorden van TerraIndex. */
export const KORRELVORM: readonly string[] = ['langwerpig', 'plat', 'bolvormig'];

/** Korrelhoekigheid en vorm gaan over zichtbare korrels: grind, zand en keitjes (deel 1, 5.2). */
export const KORRELVORM_GRONDSOORTEN: ReadonlySet<string> = new Set(['Gr', 'Z', KEITJES]);

// ─── 6. ISO-notatie ─────────────────────────────────────────────────────────

/** Interne grondsoortcode → ISO-code (deel 1, tabel 1; deel 2, tabel A.1). */
export const ISO_SOIL: Record<string, string> = {
  Gr: 'Gr', Z: 'Sa', S: 'Si', K: 'Cl', [KEITJES]: 'Co', V: 'Or', H: 'Or', DET: 'Or', BK: 'Or',
};

/** Interne bijmengingcode → ISO-code. */
export const ISO_ADMIXTURE: Record<string, string> = { gr: 'gr', z: 'sa', s: 'si', k: 'cl', ke: 'co' };

/** Mediaanterm → ISO-subfractie (deel 1, tabel 1 en NA.2). */
export const ISO_MEDIAN: Record<string, Record<string, string>> = {
  Z: {
    fijn: 'FSa', 'fijn 63-105': 'FSa1', 'fijn 105-150': 'FSa2', 'fijn 150-200': 'FSa3',
    middelgrof: 'MSa', 'middelgrof 200-300': 'MSa1', 'middelgrof 300-420': 'MSa2', 'middelgrof 420-630': 'MSa3',
    grof: 'CSa',
  },
  Gr: { fijn: 'FGr', middelgrof: 'MGr', grof: 'CGr' },
};

/** Een laag zoals de code hem beschrijft, in interne codes en termen. */
export interface IsoLayerCode {
  soilType: string; // interne code: Z, K, KEI, V …
  median: string | null; // mediaanterm ('fijn 105-150') of null
  admixtures: ReadonlyArray<{ code: string; intensity: string | null }>; // interne code + 'zwak'/'matig'/'sterk'
}

/**
 * De ISO-code van een laag: bijmengingen vóór de hoofdfractie met hun klasse, en de subfractie
 * in plaats van de hoofdfractie als die bekend is. Zand fijn 105-150, zwak siltig → `si1FSa2`.
 * Organische grondsoorten delen ISO-code Or, dus die schrijven we met hun eigen code (`V`, `H`):
 * anders valt veen niet van detritus te onderscheiden.
 */
export function formatCode(layer: IsoLayerCode): string {
  if (!layer.soilType) return '';
  const main = (layer.median && ISO_MEDIAN[layer.soilType]?.[layer.median])
    || (ISO_SOIL[layer.soilType] === 'Or' ? layer.soilType : ISO_SOIL[layer.soilType])
    || layer.soilType;
  const prefix = layer.admixtures
    .map((a) => (ISO_ADMIXTURE[a.code] ?? a.code) + (a.intensity ? INTENSITEIT.get(a.intensity) ?? '' : ''))
    .join('');
  return prefix + main;
}

/** Alle hoofd-codes die achteraan een ISO-code kunnen staan, langste eerst ('FSa2' vóór 'Sa'). */
const MAIN_CODES: ReadonlyArray<{ code: string; soilType: string; median: string | null }> = [
  ...Object.entries(ISO_MEDIAN).flatMap(([soilType, m]) =>
    Object.entries(m).map(([median, code]) => ({ code, soilType, median }))),
  ...Object.entries(ISO_SOIL).filter(([, iso]) => iso !== 'Or').map(([soilType, code]) => ({ code, soilType, median: null })),
  ...['V', 'H', 'DET', 'BK'].map((soilType) => ({ code: soilType, soilType, median: null })),
].sort((a, b) => b.code.length - a.code.length);

const ADMIXTURE_BY_ISO: Record<string, string> = Object.fromEntries(
  Object.entries(ISO_ADMIXTURE).map(([internal, iso]) => [iso, internal]),
);

/**
 * Lees een ISO-code terug (`si1FSa2`, `gr2Cl`, `sagrCo`). Hoofdletters doen er bij het intypen
 * niet toe: de hoofdfractie staat altijd achteraan, dus de plaats beslist. Null = ongeldig.
 */
export function parseCode(input: string | null | undefined): IsoLayerCode | null {
  const raw = (input ?? '').trim().toLowerCase();
  if (!raw) return null;
  const hit = MAIN_CODES.find((m) => raw.endsWith(m.code.toLowerCase()));
  if (!hit) return null;
  let rest = raw.slice(0, raw.length - hit.code.length);
  const own = GRONDSOORT_EIGEN_BIJMENGING[hit.soilType];
  const admixtures: { code: string; intensity: string | null }[] = [];
  while (rest) {
    const m = rest.match(/^([a-z]{2})([1-3])?/);
    const code = m ? ADMIXTURE_BY_ISO[m[1]] : undefined;
    if (!m || !code || code === own) return null;
    const intensity = m[2] ? [...INTENSITEIT].find(([, d]) => d === m[2])![0] : null;
    admixtures.push({ code, intensity });
    rest = rest.slice(m[0].length);
  }
  if (admixtures.length > maxAdmixtures(hit.soilType)) return null;
  return { soilType: hit.soilType, median: hit.median, admixtures };
}
